import React from 'react';
import { Mail, X } from 'lucide-react';

const SUPPORT_EMAIL = 'support@veg-ai.com';

const pageCopy = {
  about: {
    eyebrow: 'About',
    title: 'About VegAI',
    intro: 'VegAI is a food-label guidance tool that helps people review ingredients, packaged products, vegan status, possible animal-derived ingredients, allergens, source evidence, and uncertainty.',
    sections: [
      {
        title: 'What VegAI does',
        body: 'VegAI analyzes ingredient lists, product names, barcodes, and label photos to provide a practical food-guidance result. Depending on the check, VegAI may show vegan status, flagged ingredients, allergen notices, recommendations, source context, and confidence information.',
      },
      {
        title: 'Who VegAI is for',
        body: 'VegAI is designed for people who want a faster way to understand food labels and make more informed food choices. It can help with everyday vegan checks and ingredient review, but it is not a medical, allergy-safety, legal, or professional dietary service.',
      },
      {
        title: 'How VegAI improves',
        body: 'VegAI improves through source checks, verified product records, user feedback, and admin review. Accounts are optional. Signed-out Favorites, History, and Food Profile stay in that browser; signed-in users can sync them through their VegAI account.',
      },
    ],
  },
  privacy: {
    eyebrow: 'Privacy',
    title: 'Privacy Policy',
    intro: 'This Privacy Policy explains what information VegAI uses, why it is used, and how users can contact VegAI about support, corrections, or deletion requests.',
    sections: [
      {
        title: 'Information used for food checks',
        body: 'When you run a check, VegAI may process ingredient text, product names, barcodes, uploaded label images, analysis results, source evidence, and related technical details needed to provide the result and operate the service.',
      },
      {
        title: 'Service providers and sources',
        body: 'VegAI may use backend services and third-party tools such as OpenAI, USDA FoodData Central, Open Food Facts, configured web-search services, hosting providers, monitoring tools, and email/support providers to operate the app and process requests.',
      },
      {
        title: 'Guest data',
        body: 'When you are signed out, History, Favorites, and your Food Profile are saved only in the browser on that device. You can remove them with the app controls or by clearing browser storage. VegAI does not force you to create an account.',
      },
      {
        title: 'Accounts and synced data',
        body: 'If you choose to sign in with Google or an email magic link, Supabase processes your account identity and VegAI stores your synced Favorites, History, and Food Profile in Supabase Postgres. VegAI uses this information to provide account access, personalized notes, and cross-device sync.',
      },
      {
        title: 'Export and delete',
        body: 'Signed-in users can download an account-data export and permanently delete their account, synced Favorites, synced History, and Food Profile from the in-app account screen. Deleting an account does not automatically erase separate guest data stored in a browser.',
      },
      {
        title: 'Feedback, support, and corrections',
        body: 'If you submit feedback, correction details, optional contact information, or a support request, VegAI may save that information so the issue can be reviewed, answered, and used to improve product quality.',
      },
      {
        title: 'Your choices',
        body: `You can use VegAI without an account and choose not to submit optional feedback or contact information. Account export and deletion are available in the app. For other support, correction, or privacy questions, contact ${SUPPORT_EMAIL}.`,
      },
    ],
  },
  terms: {
    eyebrow: 'Terms',
    title: 'Terms of Use',
    intro: 'These Terms of Use describe the basic rules for using VegAI. By using VegAI, you agree to use it responsibly and to verify important food decisions with official product information.',
    sections: [
      {
        title: 'Informational service',
        body: 'VegAI provides informational food-label guidance. It does not replace product packaging, manufacturer statements, medical advice, allergy care, professional dietary guidance, or your own judgment.',
      },
      {
        title: 'No guarantee of accuracy',
        body: 'VegAI may be incomplete, delayed, or incorrect. Product formulas, labels, manufacturing processes, source data, and AI analysis can change or contain errors. You are responsible for confirming important decisions before relying on a result.',
      },
      {
        title: 'Responsible use',
        body: 'Do not misuse VegAI, interfere with the service, attempt unauthorized access, submit harmful content, or provide private medical details, payment information, passwords, or other sensitive information that is not needed for food analysis or support.',
      },
      {
        title: 'Changes to the service',
        body: 'VegAI may update features, result wording, policies, and these terms as the product matures. Continued use after updates means you accept the updated version.',
      },
    ],
  },
  guidance: {
    eyebrow: 'Food guidance',
    title: 'Food Guidance Disclaimer',
    intro: 'VegAI helps interpret food labels, but final responsibility stays with the product label, manufacturer, and the needs of the person eating the food.',
    emphasis: 'VegAI is not medical advice, allergy-safety advice, or a substitute for professional dietary guidance. Always check the package label and manufacturer information for allergies, severe dietary restrictions, or medical needs.',
    sections: [
      {
        title: 'Allergies and medical needs',
        body: 'If a food decision could affect your health, do not rely on VegAI alone. Review the official package label, cross-contact warnings, manufacturer information, and guidance from a qualified professional.',
      },
      {
        title: 'Formula and label changes',
        body: 'Ingredients, processing aids, cross-contact warnings, regional formulas, and packaging can change. A previous result may not apply to a later package or a different region.',
      },
      {
        title: 'Uncertainty',
        body: 'Some ingredients are context-dependent or source-dependent. VegAI may label them questionable, possibly not vegan, or uncertain when available information does not prove a clear answer.',
      },
    ],
  },
  support: {
    eyebrow: 'Support',
    title: 'Support',
    intro: 'Contact VegAI support for incorrect results, product issues, privacy questions, deletion requests, account/provider questions, or general support needs.',
    sections: [
      {
        title: 'Email support',
        body: `Email ${SUPPORT_EMAIL}. If your request is about a product result, include the product name, barcode, ingredient list, screenshot, source link, or correction details when available.`,
      },
      {
        title: 'Result corrections',
        body: 'You can also use the in-app feedback button or flag a result for admin review after running a check. VegAI may review corrections, source evidence, and user reports before updating verified product records.',
      },
      {
        title: 'Privacy and deletion requests',
        body: 'Signed-in users can export their data or delete their account inside the account screen. Guest browser History, Favorites, and Food Profile must be cleared on the device where they were saved. For other privacy requests, describe what you submitted and provide enough contact information to identify it.',
      },
    ],
  },
};

export function LaunchInfoModal({ page = 'about', onDismiss }) {
  const content = pageCopy[page] || pageCopy.about;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/45 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="launch-info-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onDismiss();
      }}
    >
      <div
        className="relative max-h-[min(90vh,760px)] w-full max-w-2xl overflow-y-auto rounded-2xl border border-gray-100 bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onDismiss}
          className="absolute right-3 top-3 rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
          aria-label={`Close ${content.title}`}
        >
          <X className="h-5 w-5" />
        </button>

        <p className="text-xs font-bold uppercase tracking-wide text-emerald-700">{content.eyebrow}</p>
        <h2 id="launch-info-title" className="mt-1 pr-10 text-xl font-black text-gray-900">
          {content.title}
        </h2>
        <p className="mt-2 text-sm leading-6 text-gray-600">{content.intro}</p>

        {content.emphasis && (
          <section className="mt-5 rounded-xl border border-amber-100 bg-amber-50 p-4">
            <p className="font-bold leading-6 text-gray-950">{content.emphasis}</p>
          </section>
        )}

        <div className="mt-5 grid gap-3 text-sm text-gray-700">
          {content.sections.map((section) => (
            <section key={section.title} className="rounded-xl border border-gray-200 p-4">
              <h3 className="font-bold text-gray-900">{section.title}</h3>
              <p className="mt-1 leading-6">{section.body}</p>
            </section>
          ))}
        </div>

        {page === 'support' && (
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="mt-5 inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white shadow-sm shadow-emerald-700/20 transition-colors hover:bg-emerald-700"
          >
            <Mail className="h-4 w-4" />
            Email support
          </a>
        )}
      </div>
    </div>
  );
}
